import React, { useMemo } from 'react';
import { useAITTheme } from '@this/shared/ui/theme';
import { getAdminFilterBarStyles, type AdminFilterSegmentPosition } from './admin_filter_bar.styles';

export interface AdminFilterSegmentOption<T extends string = string> {
  value: T;
  label: string;
}

interface AdminFilterSegmentedControlProps<T extends string> {
  name: string;
  options: readonly AdminFilterSegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
}

function getSegmentPosition(index: number, total: number): AdminFilterSegmentPosition {
  if (total === 1) return 'only';
  if (index === 0) return 'first';
  if (index === total - 1) return 'last';
  return 'middle';
}

export const AdminFilterSegmentedControl = <T extends string>({
  name,
  options,
  value,
  onChange,
  ariaLabel
}: AdminFilterSegmentedControlProps<T>) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getAdminFilterBarStyles(theme), [theme]);

  return (
    <div style={styles.segmentedControl} role="radiogroup" aria-label={ariaLabel ?? name}>
      {options.map((option, index) => {
        const selected = option.value === value;
        const position = getSegmentPosition(index, options.length);

        return (
          <label key={option.value} style={styles.segmentedLabel}>
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={selected}
              onChange={() => onChange(option.value)}
              style={styles.segmentedInput}
            />
            <span style={styles.getSegmentFaceStyle(position, selected)}>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
};
